import React, { useContext, useEffect, useRef, useState } from 'react'
import type { Map as MapLibreMap, Marker } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { GlobalContext } from '../GlobalContext'
import { useLocation } from '../context/location/LocationContext'
import { subscribe } from '../utils/pubsub'
import Spinner from './Spinner'
const mapTilerAPIKey = '1j1RqlmNhygo7wmzR2gu'

type Props = {
  locationManuallyEntered: string
  setLocationManuallyEntered: React.Dispatch<React.SetStateAction<string>>
}

export default function LocationMapVector({ locationManuallyEntered, setLocationManuallyEntered }: Props) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const emojiMarkerRef = useRef<Marker | null>(null)
  const gpsMarkerRef = useRef<Marker | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [personEmoji, setPersonEmoji] = useState('🧍‍♀️')
  const { sendMessageToWebsocket, t } = useContext(GlobalContext)
  const { locationFromGps, locationFromUser, locationAsString, setLocationFromUser, setLocationAsString, setLocationError } = useLocation()

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return

    let cancelled = false
    const container = mapContainerRef.current
    const initialCenter: [number, number] = locationFromGps
      ? [locationFromGps.lng, locationFromGps.lat]
      : [0, 0]

    import(/* webpackChunkName: "maplibre-gl" */ 'maplibre-gl').then((ml) => {
      if (cancelled || !container) return
      const maplibregl = ml.default

      const map = new maplibregl.Map({
        container,
        style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${mapTilerAPIKey}`,
        center: initialCenter,
        zoom: 13
      })
      mapRef.current = map

      const emojiEl = document.createElement('div')
      emojiEl.style.fontSize = '30px'
      emojiEl.style.width = '40px'
      emojiEl.style.height = '40px'
      emojiEl.style.textAlign = 'center'
      emojiEl.style.lineHeight = '40px'
      emojiEl.style.borderRadius = '50%'
      emojiEl.style.background = 'rgba(255,255,255,0.85)'
      emojiEl.style.boxShadow = '0 0 4px rgba(0,0,0,0.4)'
      emojiEl.innerText = '🧍‍♀️'
      emojiMarkerRef.current = new maplibregl.Marker({ element: emojiEl })
        .setLngLat([map.getCenter().lng, map.getCenter().lat])
        .addTo(map)

      const gpsEl = document.createElement('div')
      gpsEl.style.width = '14px'
      gpsEl.style.height = '14px'
      gpsEl.style.borderRadius = '50%'
      gpsEl.style.background = '#0d6efd'
      gpsEl.style.boxShadow = '0 0 6px rgba(0,0,0,0.5)'
      gpsMarkerRef.current = new maplibregl.Marker({ element: gpsEl })
        .setLngLat(initialCenter)
        .addTo(map)

      map.on('move', () => {
        setPersonEmoji('🚶‍♂️')
        emojiMarkerRef.current?.setLngLat([map.getCenter().lng, map.getCenter().lat])
      })
      map.on('moveend', (e: any) => {
        const c = map.getCenter()
        if (c.lat === 0 && c.lng === 0) return
        if (!e.originalEvent) return
        setLocationFromUser({ lat: c.lat, lng: c.lng })
        setPersonEmoji('🧍‍♀️')
        emojiMarkerRef.current?.setLngLat([c.lng, c.lat])
      })

      let tileErrorCount = 0
      map.on('load', () => setMapLoaded(true))
      map.on('error', (e: any) => {
        if (!e.tile) {
          setLocationError(t('UIElements.LocationMapVector.mapFailedToLoad'))
        } else {
          tileErrorCount++
          if (tileErrorCount >= 5) setLocationError(t('UIElements.LocationMapVector.mapFailedToLoad'))
        }
      })
    })

    return () => {
      cancelled = true
      mapRef.current?.remove()
      mapRef.current = null
      emojiMarkerRef.current = null
      gpsMarkerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!locationFromGps) return
    const center = mapRef.current?.getCenter()
    const threshold = 0.01
    const bigChange = !center ||
      Math.abs(center.lat - locationFromGps.lat) > threshold ||
      Math.abs(center.lng - locationFromGps.lng) > threshold

    gpsMarkerRef.current?.setLngLat([locationFromGps.lng, locationFromGps.lat])
    if (bigChange) {
      mapRef.current?.easeTo({ center: [locationFromGps.lng, locationFromGps.lat], duration: 300 })
    }
  }, [locationFromGps])

  useEffect(() => {
    const el = emojiMarkerRef.current?.getElement()
    if (el) el.textContent = personEmoji
  }, [personEmoji])

  useEffect(() => {
    const coords = locationFromUser || locationFromGps
    if (mapLoaded && coords && coords.lat !== 0 && coords.lng !== 0) {
      const unsubscribe = subscribe('addressFoundForCoordinates', (data: any) => {
        if (data) setLocationAsString(data)
        unsubscribe()
      })
      sendMessageToWebsocket(JSON.stringify({
        action: 'checklistsClientApp',
        subAction: 'findAddressForCoordinates',
        location: coords
      }))
    }
  }, [mapLoaded, locationFromGps, locationFromUser])

  const lat = locationFromUser?.lat ?? locationFromGps?.lat ?? null
  const lng = locationFromUser?.lng ?? locationFromGps?.lng ?? null
  const coordinates = lat && lng
    ? t('UIElements.LocationMapVector.coordinates', { lat: parseFloat(String(lat)).toFixed(5), lng: parseFloat(String(lng)).toFixed(5) })
    : null

  return <div className="fixed top-0 left-0 h-screen w-screen">
    <div ref={mapContainerRef} className="w-screen h-screen" style={{ zIndex: 0 }} />
    <div className='fixed w-full' style={{ zIndex: 50, bottom: '70px', margin: '0 auto' }}>
      <div className='w-full flex flex-col items-center justify-center px-6'>
        <div className='w-full max-w-2xl bg-white/70 rounded p-2'>
          {!locationFromGps && <Spinner size='large'>{t('QuestionPages.QuestionLocation.gettingLocation')}</Spinner>}
          {locationAsString && <p className="font-bold">{locationAsString}</p>}
          {coordinates && <p className="text-xs">{coordinates}</p>}
        </div>
      </div>
    </div>
  </div>
}
